import { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDeeplinkRedirect } from './useDeeplinkRedirect';
import { useIsUnifiedPromoBonusSystemEnabled } from './useIsUnifiedPromoBonusSystemEnabled';
import { PROMO, SLOTS } from '../constants/productTypes';
import { ROOT } from '../constants/routeTemplates';
import {
  REDIRECT_TO_UNIFIED_PROMO_LOBBY_ADDITIONAL_DATA,
  REDIRECT_TO_UNIFIED_PROMO_LOBBY_KEY,
  UNIFIED_PROMO_BONUSES_REDIRECTS,
} from '../constants/bonuses';

const FILTER_PARAM = 'filter';
const CASINO_PARAM_VALUE = 'casino';

export const getPromoBonusUrl = () => {
  const searchParams = new URLSearchParams();
  searchParams.append(FILTER_PARAM, CASINO_PARAM_VALUE);
  searchParams.append(FILTER_PARAM, SLOTS);
  return `?${searchParams.toString()}`;
};

export const usePromoBonusUrl = (): string => {
  return useMemo(() => getPromoBonusUrl(), []);
};
export const usePromoBonusesPageRedirectCallback = () => {
  const deeplinkRedirect = useDeeplinkRedirect();
  const url = usePromoBonusUrl();

  return useCallback(() => {
    deeplinkRedirect(PROMO, url);
  }, [deeplinkRedirect, url]);
};

const BASE_PROMO_BONUS_PATH = '/offers/casinoBonus/';

export const usePromoBonusRedirect = () => {
  const deeplinkRedirect = useDeeplinkRedirect();
  return useCallback(
    (promotionId) => {
      const path = BASE_PROMO_BONUS_PATH + promotionId;
      deeplinkRedirect(PROMO, path);
    },
    [deeplinkRedirect]
  );
};

export const usePromoBonusPageRedirect = (
  pathIfDisabled = '',
  promotionId = '',
  isEnabled = true
) => {
  const isPromoBonusSystemEnabled = useIsUnifiedPromoBonusSystemEnabled();
  const history = useHistory();

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    if (!isPromoBonusSystemEnabled) {
      if (pathIfDisabled) {
        history?.replace(pathIfDisabled);
      }
      return;
    }

    const isToBonusRedirect = !!promotionId;

    const routeState = isToBonusRedirect
      ? {
          [REDIRECT_TO_UNIFIED_PROMO_LOBBY_KEY]:
            UNIFIED_PROMO_BONUSES_REDIRECTS.TO_BONUS,
          [REDIRECT_TO_UNIFIED_PROMO_LOBBY_ADDITIONAL_DATA]: promotionId,
        }
      : {
          [REDIRECT_TO_UNIFIED_PROMO_LOBBY_KEY]:
            UNIFIED_PROMO_BONUSES_REDIRECTS.TO_ALL_BONUS_PAGE,
        };

    history.replace(ROOT, routeState);
  }, [
    history,
    isPromoBonusSystemEnabled,
    pathIfDisabled,
    promotionId,
    isEnabled,
  ]);
};

export const useDashboardPromoLobbyRedirect = () => {
  const { state } = useLocation();
  const history = useHistory();
  const promoBonusesPageRedirect = usePromoBonusesPageRedirectCallback();
  const promoBonusRedirect = usePromoBonusRedirect();

  useEffect(() => {
    const redirectToPromoLobbyKey =
      state?.[REDIRECT_TO_UNIFIED_PROMO_LOBBY_KEY];

    if (!redirectToPromoLobbyKey) {
      return;
    }

    const additionalRouteData =
      state?.[REDIRECT_TO_UNIFIED_PROMO_LOBBY_ADDITIONAL_DATA];

    history.replace(ROOT, {});
    if (additionalRouteData) {
      promoBonusRedirect(additionalRouteData);
    } else {
      promoBonusesPageRedirect();
    }
  }, [state, promoBonusRedirect, promoBonusesPageRedirect, history]);
};
